import { union } from 'lodash';

/** A colourway id as this screen holds it — a Mongo id already serialised to a string. */
export type ColourId = string;

/**
 * A colourway id exactly as it can arrive from the API: normally an already-serialised string,
 * but a populated / non-serialised document reaches the client as an object whose `toString()`
 * yields the same id, which is what the dropdown matches on.
 */
export interface SerialisableId {
  toString(): string;
}

/**
 * A colourway id list off the product payload. The API omits the field on products that have
 * never had a colourway, and legacy rows can carry blank entries, so both are part of the
 * contract.
 */
export type ColourIdPayload = Array<ColourId | SerialisableId | null | undefined> | null | undefined;

/**
 * Normalises a raw id list from the API into plain string ids, dropping empty entries.
 * @param ids Raw value off the product payload.
 * @returns String ids, in source order.
 */
const toIdList = (ids: ColourIdPayload): ColourId[] => {
  if (!Array.isArray(ids)) {
    return [];
  }
  return ids.filter((id): id is ColourId | SerialisableId => id !== null && id !== undefined && id !== '').map((id) => String(id));
};

/**
 * Folds the colourways a product already consumes downstream into its own colourway selection.
 *
 * `orderColors` (GET /development-orders/:id, built by `getIfColourInUse`) lists every colourway
 * reached through a Purchase Order line, a BOM row, a Product Costing row, a Sample or a Design
 * Detail — five sources, wider than the three behind `orderSupplierId`. Those rows are rendered
 * disabled in the Colourway(s) dropdown, while the tick state comes from `colourLibraries` — so a
 * colourway attached from one of those modules without ever being picked on the product itself
 * rendered disabled AND unticked. Merging on hydrate keeps `colourLibraries` a superset of
 * `orderColors`, which is the invariant the retain-on-deselect branch already assumes.
 *
 * `retrieveDevelopmentOrderById` merges the same two lists before it responds, so this is a guard
 * rather than the primary fix — it keeps the screen correct against an older API build.
 *
 * @param savedColours The product's own `colourLibraries` array.
 * @param inUseColours The `orderColors` array returned alongside it.
 * @returns De-duplicated string ids — saved selection first, downstream-only colourways appended.
 */
export const mergeInUseColours = (savedColours: ColourIdPayload, inUseColours: ColourIdPayload): ColourId[] =>
  union(toIdList(savedColours), toIdList(inUseColours));
